import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import useBalances from '@xanadu/ui-shared/hooks/useBalances';
import { roundDownSmallValue } from '@xanadu/ui-shared/utils/numbers';
import Spinner from '@xanadu/ui-shared/components/widgets/Spinner';
import Button from '@xanadu/ui-shared/components/widgets/Button';
import Currency from '../../../containers/Currency';

import IconBonus from '../../../../images/icons/casino/bonus-icon.svg';
import IconAlert from '../../../../images/icons/misc/icon-alert.svg';
import IconAlertWhite from '../../../../images/icons/misc/icon-alert_white.svg';

import { MOBILE_BREAKPOINT } from '../../../constants';
import styles from './Balances.module.scss';

const Balances = ({
  balance,
  balanceLoading,
  casinoPromotions,
  edgeStatus,
  depositRequest,
  widthSize,
  rightSidebarContent,
  userSegmentCategory,
  loadingNetDeposit,
  netDepositError,
  updateRightSidebar,
  displayModal,
  netDepositLimit,
  requestNetDepositLimit,
  userAffordabilityInfoProvided,
}) => {
  const isMobile = widthSize <= MOBILE_BREAKPOINT;

  const [depositButtonText, setDepositButtonText] = useState('Deposit');

  const {
    walletAvailable,
    showBonusIcon,
    bonusFreeFunds,
    freeSpins,
    bonusAmount,
    showFreeBetsBox,
    userAffordabilityInfoIsNotProvided,
    onDepositClick,
  } = useBalances({
    isMobile,
    edgeStatus,
    balance,
    balanceLoading,
    depositRequest,
    casinoPromotions,
    netDepositLimit,
    requestNetDepositLimit,
    userAffordabilityInfoProvided,
  });

  const bonusIconClass = classNames({
    [styles.bonusToggle]: true,
    '.gtm_cta': true,
  });

  const BonusIconLink = () => (
    <Link
      className={bonusIconClass}
      to="/promotions/casino"
      data-hook="casino-bonus-icon"
      onClick={() => {
        updateRightSidebar({
          content: null,
          visible: false
        });
      }}
    >
      <IconBonus className="bonus-toggle-icon" />
    </Link>
  );

  const mainClass = classNames({
    [styles.main]: true,
    '.gtm_cta': walletAvailable,
  });

  const availableClass = classNames([
    styles.balanceLabel,
    styles['balanceLabel--available']
  ]);

  const exposureClass = classNames([
    styles.balanceLabel,
    styles['balanceLabel--exposure']
  ]);

  const freeBetsClass = classNames([
    styles.balanceLabel,
    styles['balanceLabel--freeBets']
  ]);

  const freeSpinsClass = classNames([
    styles.balanceLabel,
    styles['balanceLabel--freeSpins']
  ]);

  const heldFundsClass = classNames([
    styles.balanceLabel,
    styles['balanceLabel--heldFunds']
  ]);

  const depositButtonClass = classNames({
    [styles.button]: true,
    [styles['button--disabled']]: loadingNetDeposit
  });

  const loadingSpinner = (
    <div className={styles.loader}>
      <Spinner size="medium" theme="light" />
    </div>
  );

  const hasEKYCErrorMessage = useMemo(() => (
    netDepositError.includes('EKYC check failed for the account:') ||
    netDepositError.includes('Net deposit limits can\'t be set')
  ), [netDepositError]);


  // Segment Category is one of "V", "X", "Y", "Z"
  const continueGBGFVCCheck = useMemo(() => (
    userSegmentCategory !== 'V' && userAffordabilityInfoIsNotProvided
  ), [userSegmentCategory, userAffordabilityInfoIsNotProvided]);

  let balanceContent = (
    <Fragment>
      {walletAvailable ? (
        <div className={styles.balances}>
          <div className={styles.balanceRow}>
            <div className={availableClass}>
              Available
            </div>
            <div className={styles.balanceValue} data-hook="availablebalance">
              {walletAvailable ? (
                <Currency value={roundDownSmallValue(balance.freeFunds)} round="floor" />
              ) : 'n/a'}
            </div>
          </div>
          {!isMobile && (
            <div className={styles.balanceRow}>
              <div className={exposureClass}>
                EXPOSURE
              </div>
              <div className={styles.balanceValue} data-hook="exposurebalance">
                <Currency value={roundDownSmallValue(balance.exposure)} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <span className={styles.unavailable}>Wallet unavailable</span>
      )}
    </Fragment>
  );

  if (!isMobile && continueGBGFVCCheck && walletAvailable) {
    balanceContent = (
      <Fragment>
        <div className={styles.welcomeContent}>
          <span className={styles.title}>Welcome to Matchbook</span>
          <span className={styles.subtitle}>Deposit now to get started</span>
        </div>
      </Fragment>
    );
  }

  if (!isMobile && continueGBGFVCCheck && (loadingNetDeposit)) {
    balanceContent = (
      <Fragment>
        {(loadingNetDeposit) && loadingSpinner}
        <div className={styles.welcomeContent}>
          <span className={styles.title}>Please wait a moment</span>
        </div>
      </Fragment>
    );
  }

  if (!isMobile && continueGBGFVCCheck && netDepositError) {
    balanceContent = (
      <Fragment>
        <IconAlertWhite className={styles['alertIcon--error']} />
        <div className={styles.welcomeContent}>
          {
            hasEKYCErrorMessage ? (
              <Fragment>
                <span className={styles.title}>Further Verification Needed</span>
                <span className={styles['subtitle--dark']}>Please upload documents</span>
              </Fragment>
            ) : (
              <Fragment>
                <span className={styles.title}>Something went wrong</span>
                <span className={styles['subtitle--dark']}>Unable to complete verification</span>
              </Fragment>
            )
          }
        </div>
      </Fragment>
    );
  }

  useEffect(() => {
    if (netDepositError) {
      if (hasEKYCErrorMessage) {
        displayModal({
          show: true,
          section: 'deposit'
        });
        setDepositButtonText('Upload');
      } else {
        setDepositButtonText('Try Again');
      }
    } else {
      setDepositButtonText('Deposit');
    }
  }, [netDepositError, hasEKYCErrorMessage]);

  const depositButton = (
    <Button
      className={depositButtonClass}
      sizeType="regular"
      buttonType="primary"
      onClick={onDepositClick}
      aria-label="Deposit"
      disabled={loadingNetDeposit}
    >
      {depositButtonText}
    </Button>
  );

  return (
    <div
      className={mainClass}
      role="button"
      onClick={() => {
        updateRightSidebar({
          content: rightSidebarContent !== 'account' ? 'account' : null,
          visible: !(rightSidebarContent === 'account')
        });
      }}
      data-hook="availablebalanceclick"
    >
      {!isMobile && showBonusIcon && <BonusIconLink />}
      {showFreeBetsBox && (
        <div className={styles.box}>
          <div className={styles.balances}>
            {Boolean(bonusFreeFunds) && bonusFreeFunds >= 0.1 && (
              <div className={styles.balanceRow}>
                <div className={freeBetsClass}>
                  BONUSES
                </div>
                <div className={styles.balanceValue} data-hook="bonus-value">
                  <Currency value={roundDownSmallValue(bonusFreeFunds)} round="floor" />
                </div>
              </div>
            )}
            {Boolean(freeSpins) && (
              <div className={styles.balanceRow}>
                <div className={freeSpinsClass}>
                  FREE SPINS
                </div>
                <div className={styles.balanceValue}>
                  {`x${freeSpins}`}
                </div>
              </div>
            )}
            {Boolean(bonusAmount) && (
              <div className={styles.balanceRow}>
                <div className={heldFundsClass}>
                  CASINO BONUS
                </div>
                <div className={styles.balanceValue}>
                  <Currency value={roundDownSmallValue(bonusAmount)} round="floor" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={styles.box}>
        {balanceLoading ? loadingSpinner : balanceContent}
        {!balanceLoading && (
          <Fragment>
            {walletAvailable ? (
              depositButton
            ) : (
              <IconAlert className={styles.alertIcon} />
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};

Balances.propTypes = {
  balance: PropTypes.instanceOf(Object).isRequired,
  balanceLoading: PropTypes.bool.isRequired,
  edgeStatus: PropTypes.number,
  rightSidebarContent: PropTypes.string,
  updateRightSidebar: PropTypes.func.isRequired,
  depositRequest: PropTypes.func.isRequired,
  requestNetDepositLimit: PropTypes.func.isRequired,
  displayModal: PropTypes.func.isRequired,
  widthSize: PropTypes.number.isRequired,
  casinoPromotions: PropTypes.instanceOf(Map),
  netDepositLimit: PropTypes.number,
  loadingNetDeposit: PropTypes.bool.isRequired,
  netDepositError: PropTypes.string,
  userAffordabilityInfoProvided: PropTypes.bool,
  userSegmentCategory: PropTypes.string,
};

export default Balances;
